import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDays, diff, key, parse, todayKey } from './dates.js';
import { fetchCalendarAll } from './api.js';

/**
 * Up to three alternative stays a single home can genuinely host, filling
 * three distinct slots: (1) the nearest same-duration window, (2) the
 * nearest similar-duration window starting AFTER the requested check-in,
 * (3) the nearest similar-duration window starting BEFORE it. "Similar"
 * allows ±1 night, preferring the exact length. Empty slots backfill with
 * the next-nearest unused window. Uses only known-free nights (nothing
 * past the home's loaded window) and respects each check-in date's own
 * minimum stay. Returned in date order as {in, out}.
 */
export function suggestStays(home, inDate, nights) {
  if (!home || !home.loadedUntil || nights < 1) return [];
  const first = todayKey(); // same-day check-ins are allowed
  const lens = [...new Set([nights, Math.max(1, nights - 1), nights + 1])];
  const candidates = [];
  for (let d = first; d <= home.loadedUntil; d = addDays(d, 1)) {
    for (const len of lens) {
      if (addDays(d, len - 1) > home.loadedUntil) continue;
      if ((home.minNightsByDate[d] ?? home.minNights) > len) continue;
      let ok = true;
      for (let i = 0, n = d; i < len; i++, n = addDays(n, 1)) {
        if (home.blocked.has(n)) { ok = false; break; }
      }
      if (ok) candidates.push({ in: d, out: addDays(d, len), len });
    }
  }
  if (!candidates.length) return [];

  // One option per check-in date (length closest to the request wins), so
  // the three slots are genuinely different dates - never the same window
  // trimmed or stretched by a night.
  const byStart = new Map();
  for (const c of candidates) {
    const cur = byStart.get(c.in);
    if (!cur || Math.abs(c.len - nights) < Math.abs(cur.len - nights)) byStart.set(c.in, c);
  }
  const reps = [...byStart.values()];

  const byScore = (a, b) =>
    Math.abs(diff(inDate, a.in)) - Math.abs(diff(inDate, b.in)) ||
    Math.abs(a.len - nights) - Math.abs(b.len - nights);
  const used = new Set();
  const picked = [];
  const take = (c) => {
    if (c && !used.has(c.in)) { used.add(c.in); picked.push(c); }
  };
  take(reps.filter((c) => c.len === nights).sort(byScore)[0]);
  take(reps.filter((c) => c.in > inDate && !used.has(c.in)).sort(byScore)[0]);
  take(reps.filter((c) => c.in < inDate && !used.has(c.in)).sort(byScore)[0]);
  for (const c of reps.sort(byScore)) {
    if (picked.length >= 3) break;
    take(c);
  }
  return picked
    .sort((a, b) => (a.in < b.in ? -1 : 1))
    .map((c) => ({ in: c.in, out: c.out }));
}

/**
 * Cross-property calendar state for the homepage search.
 *
 * Loads one calendar-all window (~8 months up front, further 12-month windows
 * on demand - mirrors PropertyCalendar) and derives an aggregate view for the
 * current party size entirely client-side: a date is blocked only when NO
 * capacity-compatible home is known to have it free. Homes whose fetch failed
 * contribute nothing - their dates are unknown, never free.
 */
export default function useAggregateCalendar(party, checkIn = '', checkOut = '') {
  // slug => { maxGuests, blocked: Set, minNightsByDate, minNights, loadedUntil }
  const [homes, setHomes] = useState(null);
  const [loadedUntil, setLoadedUntil] = useState('');
  const [error, setError] = useState(null);
  const loadedUntilRef = useRef('');
  const fetchingRef = useRef(false);

  const merge = useCallback((data, to) => {
    setHomes((prev) => {
      const next = { ...(prev || {}) };
      for (const h of data.homes || []) {
        const cur = next[h.slug];
        if (!h.ok) {
          // Failed window: keep whatever is already known; the home's
          // loadedUntil simply doesn't advance, so new dates stay unknown.
          if (!cur) next[h.slug] = { maxGuests: h.maxGuests, blocked: new Set(), minNightsByDate: {}, minNights: 1, loadedUntil: '' };
          continue;
        }
        next[h.slug] = {
          maxGuests: h.maxGuests,
          blocked: new Set([...(cur?.blocked || []), ...(h.blocked || [])]),
          minNightsByDate: { ...(cur?.minNightsByDate || {}), ...(h.minNightsByDate || {}) },
          minNights: h.minNights || cur?.minNights || 1,
          loadedUntil: to,
        };
      }
      return next;
    });
    loadedUntilRef.current = to;
    setLoadedUntil(to);
  }, []);

  useEffect(() => {
    const from = todayKey();
    const to = addDays(from, 238); // ~8 months
    fetchCalendarAll(from, to)
      .then((d) => merge(d, to))
      .catch((err) => setError(err.message));
  }, [merge]);

  const handleNeedMonths = useCallback(
    (offset) => {
      const t = parse(todayKey());
      const neededEnd = key(new Date(t.getFullYear(), t.getMonth() + offset + 1, 0));
      if (!loadedUntilRef.current || loadedUntilRef.current >= neededEnd || fetchingRef.current) return;
      fetchingRef.current = true;
      const from = addDays(loadedUntilRef.current, 1);
      const to = addDays(from, 364); // the API caps each request at ~18 months
      fetchCalendarAll(from, to)
        .then((d) => {
          merge(d, to);
          fetchingRef.current = false;
          handleNeedMonths(offset); // still short of what was requested? chain the next window
        })
        .catch(() => {
          fetchingRef.current = false; // the next navigation retries
        });
    },
    [merge]
  );

  // Aggregate for the current party size and (while choosing a checkout) the
  // selected check-in. Recomputed client-side - no refetch. Per-home
  // reasoning throughout: a night reads free only when a single specific
  // home is known free, a check-in is offered only where some home can host
  // its own minimum stay, and checkouts are capped at the furthest night any
  // ONE home free at the check-in can cover - never a stitched-together
  // union no real home could honour.
  const aggregate = useMemo(() => {
    if (!homes) return null;
    const compatible = Object.values(homes).filter((h) => h.maxGuests >= party);
    if (!compatible.length) {
      return { blocked: [], minNights: 1, minNightsByDate: {}, canStart: () => false, empty: true };
    }
    const horizon = compatible.reduce((m, h) => (h.loadedUntil > m ? h.loadedUntil : m), '');
    const today = todayKey();

    // runs[i][d] = consecutive known-free nights in home i starting at d
    // (0 = blocked or beyond that home's loaded window - unknown, never free).
    const runs = compatible.map((h) => {
      const r = {};
      let run = 0;
      for (let d = horizon; horizon && d >= today; d = addDays(d, -1)) {
        const free = h.loadedUntil && d <= h.loadedUntil && !h.blocked.has(d);
        run = free ? run + 1 : 0;
        r[d] = run;
      }
      return r;
    });
    const minFor = (h, d) => h.minNightsByDate[d] ?? h.minNights;

    const blocked = [];
    const minNightsByDate = {};
    const startable = new Set();
    for (let d = today; horizon && d <= horizon; d = addDays(d, 1)) {
      let free = false;
      let minReq = Infinity;
      compatible.forEach((h, i) => {
        if (runs[i][d] > 0) {
          free = true;
          // The date's advertised minimum is the weakest among homes that can
          // actually START a stay here, not merely sleep the night.
          if (runs[i][d] >= minFor(h, d)) {
            startable.add(d);
            minReq = Math.min(minReq, minFor(h, d));
          }
        }
      });
      if (!free) blocked.push(d);
      else if (minReq > 1 && minReq !== Infinity) minNightsByDate[d] = minReq;
    }

    // Choosing a checkout: only homes free (with a valid start) at the
    // check-in count, and nights past the furthest any of them can cover are
    // blocked - even if some other home happens to be free then.
    const endMode = checkIn && !checkOut && horizon && checkIn >= today && checkIn <= horizon;
    if (endMode) {
      let maxRun = 0;
      let minReq = Infinity;
      compatible.forEach((h, i) => {
        const run = runs[i][checkIn];
        if (run >= minFor(h, checkIn)) {
          maxRun = Math.max(maxRun, run);
          minReq = Math.min(minReq, minFor(h, checkIn));
        }
      });
      if (minReq !== Infinity) minNightsByDate[checkIn] = minReq;
      const blockedSet = new Set(blocked);
      for (let d = addDays(checkIn, maxRun); d <= horizon; d = addDays(d, 1)) {
        if (!blockedSet.has(d)) blocked.push(d);
      }
      blocked.sort();
    }

    const minNights = Math.min(...compatible.map((h) => h.minNights));
    return {
      blocked,
      minNights,
      minNightsByDate,
      canStart: (d) => startable.has(d),
      empty: false,
    };
  }, [homes, party, checkIn, checkOut]);

  return { aggregate, homes, loadedUntil, error, onNeedMonths: handleNeedMonths };
}
